import React from 'react';
import type { BrandWithMetrics } from '@/hooks/useAllBrandMetrics';
import { formatCurrency } from '@/lib/utils/currency';

export interface BrandCardProps {
  brand: BrandWithMetrics;
  isSelected: boolean;
  onSelect: (brand: BrandWithMetrics) => void;
  userCountry?: string | null;
}

export const BrandCard: React.FC<BrandCardProps> = ({
  brand,
  isSelected,
  onSelect,
  userCountry,
}) => {
  const formatROI = (roi: number) => `${roi}%`;

  return (
    <div
      className={`p-4 rounded-xl shadow-sm border transition-all cursor-pointer ${
        isSelected
          ? 'border-purple-500 bg-purple-50'
          : 'border-gray-200 bg-white hover:border-purple-300'
      }`}
      onClick={() => onSelect(brand)}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-14 h-14 bg-white rounded-lg border border-gray-200 shadow-sm flex items-center justify-center overflow-hidden shrink-0">
            {brand.logoUrl ? (
              <img
                src={brand.logoUrl}
                alt={brand.name}
                className="object-contain w-full h-full p-1"
              />
            ) : (
              <span className="text-purple-600 font-bold text-lg">{brand.name?.[0]}</span>
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{brand.name}</h3>
            <p className="text-sm text-gray-600">{brand.industry || 'No industry specified'}</p>
          </div>
        </div>
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
          Active
        </span>
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-gray-600">Total Campaigns:</span>
          <span className="font-medium text-gray-900 ml-1">{brand.totalCampaigns}</span>
        </div>
        <div>
          <span className="text-gray-600">Active:</span>
          <span className="font-medium text-gray-900 ml-1">{brand.activeCampaigns}</span>
        </div>
        <div>
          <span className="text-gray-600">Spent:</span>
          <span className="font-medium text-gray-900 ml-1">
            {formatCurrency(brand.totalSpent, userCountry)}
          </span>
        </div>
        <div>
          <span className="text-gray-600">Avg ROI:</span>
          <span className="font-medium text-green-600 ml-1">
            {brand.averageROI ? formatROI(brand.averageROI) : 'N/A'}
          </span>
        </div>
      </div>
    </div>
  );
};
